import { useEffect, useRef, useState } from "react";
import { FONTS, TEXT_STYLES, PALETTE, textStyleProps } from "./storyOverlays";
import "./storyStudio.css";

/** Compositore testo inline stile IG: scrivi sul canvas, carosello font e stile a pillola. */
export default function TextComposer({ initial, onDone, onCancel }) {
  const [draft, setDraft] = useState(initial);
  const [kb, setKb] = useState(0);
  const taRef = useRef(null);

  // solleva i controlli sopra la tastiera (iOS/Android)
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const upd = () => setKb(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    vv.addEventListener("resize", upd);
    upd();
    return () => vv.removeEventListener("resize", upd);
  }, []);

  useEffect(() => {
    const ta = taRef.current;
    if (ta) {
      ta.focus();
      ta.setSelectionRange(ta.value.length, ta.value.length);
    }
  }, []);

  const styleIdx = Math.max(0, TEXT_STYLES.findIndex((s) => s.id === draft.style));
  const cycleStyle = () =>
    setDraft({ ...draft, style: TEXT_STYLES[(styleIdx + 1) % TEXT_STYLES.length].id });

  // anteprima live: gradient/contorno resi visibili nella textarea
  const preview = textStyleProps(draft);
  if (draft.style === "gradient") {
    preview.background = "transparent";
    preview.color = "#FF8A5C";
    preview.textShadow = "0 2px 12px rgba(255,59,92,0.5)";
  }
  if (draft.style === "outline") {
    preview.color = "rgba(255,255,255,0.92)";
  }

  const commit = () => {
    const text = (draft.text || "").trim();
    if (!text) { onCancel(); return; }
    onDone({ ...draft, text });
  };

  const rows = Math.min(6, (draft.text.match(/\n/g) || []).length + 1);

  return (
    <div className="stc-root" data-testid="studio-text-composer" onClick={commit}>
      <div className="stc-top" onClick={(e) => e.stopPropagation()}>
        <button className="stc-cancel" onClick={onCancel} data-testid="studio-text-cancel">Annulla</button>
        <button className="stc-done" onClick={commit} data-testid="studio-text-add">Fine</button>
      </div>

      <textarea
        ref={taRef}
        className="stc-input"
        style={preview}
        rows={rows}
        value={draft.text}
        placeholder="Scrivi..."
        maxLength={220}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraft({ ...draft, text: e.target.value })}
        data-testid="studio-text-input"
      />

      <div className="stc-controls" style={{ bottom: kb + 14 }} onClick={(e) => e.stopPropagation()}>
        <div className="stc-row">
          <button
            className={`stc-stylepill ${draft.style !== "plain" ? "stc-on" : ""}`}
            onClick={cycleStyle}
            aria-label="Stile testo"
            data-testid="studio-style-cycle"
          >
            Aa
          </button>
          <div className="stc-row" style={{ flex: 1 }}>
            {FONTS.map((f) => (
              <button
                key={f.id}
                className={`stc-font ${draft.font === f.id ? "stc-on" : ""}`}
                style={{ fontFamily: f.css, fontWeight: f.weight }}
                onClick={() => setDraft({ ...draft, font: f.id })}
                data-testid={`studio-font-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="stc-row" style={{ justifyContent: "center" }}>
          {PALETTE.map((c) => (
            <button
              key={c}
              className={`stc-swatch ${draft.color === c ? "stc-on" : ""}`}
              style={{ background: c }}
              onClick={() => setDraft({ ...draft, color: c, bgColor: c })}
              aria-label={c}
            />
          ))}
        </div>
        <p className="stc-stylename" data-testid="studio-style-name">{TEXT_STYLES[styleIdx]?.label}</p>
      </div>
    </div>
  );
}
